import React from 'react';
import { toastr } from 'react-redux-toastr';
import isNil from 'lodash/isNil';
import type { Props, UIProps, SaveFinalDecisionPayload } from './types';
import messages from '../messages';
import { post } from '../../../utils/api';
import { AppContext } from '../../../containers/App/context';

export function useFinalDecisionFormUIProps({
  intl,
  screeningId,
  finalScreeningStatus,
  reasonForDecision,
  refetchHouseholdScreening,
  decisionDate,
  decisionUserFullName,
  isApplicationCanceledOrDenied,
  didCompleteAllScreeningMethods,
  promptToaster,
  adverseActionDeadlineDays,
  applicationId,
  screeningLetterTypes,
  organizationId,
  propertyId,
  screeningData,
  isResident = false,
}: Props): UIProps {
  const { userId } = React.useContext(AppContext);
  const [isReadOnly, setIsReadOnly] = React.useState(true);
  const hasExistingFinalDecision = React.useMemo(
    () => !isNil(finalScreeningStatus),
    [finalScreeningStatus],
  );
  const isCreateAdverseActionLetterShown = React.useMemo(
    () =>
      hasExistingFinalDecision &&
      finalScreeningStatus !== 'APPROVED' &&
      screeningData?.screeningStatus !== 'error' &&
      isReadOnly,
    [
      hasExistingFinalDecision,
      finalScreeningStatus,
      screeningData?.screeningStatus,
      isReadOnly,
    ],
  );

  const handleSaveFinalDecision = async (payload: SaveFinalDecisionPayload) => {
    if (!screeningId) {
      return;
    }

    await post(
      '/screening-service/screening-final-decisions',
      JSON.stringify({
        finalStatus: payload.finalScreeningStatus,
        reason: payload.reasonForDecision,
        screeningId,
        createdById: userId,
        applicationId,
        propertyId,
      }),
    )
      .then(() => {
        refetchHouseholdScreening();
        setIsReadOnly(true);
        toastr.success(
          intl.formatMessage(messages.success),
          intl.formatMessage(messages.finalDecisionSaved),
        );
      })
      .catch(() => {
        toastr.error(
          intl.formatMessage(messages.error),
          intl.formatMessage(messages.errorSavingFinalDecision),
        );
      });
  };

  React.useEffect(() => {
    if (didCompleteAllScreeningMethods) {
      setIsReadOnly(hasExistingFinalDecision);
    } else {
      setIsReadOnly(true);
    }
  }, [hasExistingFinalDecision, didCompleteAllScreeningMethods]);

  return {
    isResident,
    isReadOnly,
    intl,
    onEditFinalDecision: () => setIsReadOnly(false),
    onSaveFinalDecision: handleSaveFinalDecision,
    decisionDate,
    decisionUserFullName,
    finalScreeningStatus,
    reasonForDecision,
    onCancelEdit: () => setIsReadOnly(true),
    isCreateAdverseActionLetterShown,
    isApplicationCanceledOrDenied,
    promptToaster,
    adverseActionDeadlineDays,
    applicationId,
    screeningLetterTypes,
    organizationId,
    propertyId,
    screeningData,
  };
}
